import { useEffect, useState } from 'react';
import { Eye20Regular, EyeOff20Regular, Warning20Regular } from '@fluentui/react-icons';
import {
  getAiSettings,
  saveAiSettings,
  type AiSettings,
} from '../orcamentos/orcamentosApi';

const ANTHROPIC_MODELS = [
  { value: 'claude-opus-5-5', label: 'Claude Opus 5.5 (recomendado para plantas técnicas)' },
  { value: 'claude-opus-4-7', label: 'Claude Opus 4.7' },
  { value: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
  { value: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (equilibrado, mais barato)' },
  { value: 'claude-opus-4-5', label: 'Claude Opus 4.5 (legado)' },
  { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5 (legado)' },
];

const DEFAULT_MODEL = 'claude-opus-5-5';

// Estes modelos raciocinam antes de responder e, com menos tokens, cortam os documentos grandes.
const MODELS_NEEDING_MORE_TOKENS = ['claude-opus-5-5', 'claude-sonnet-5-5'];
const MIN_TOKENS_FOR_RECENT_MODELS = 64000;

export default function AiSettingsTab() {
  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [maxTokens, setMaxTokens] = useState(4096);
  const [maxPages, setMaxPages] = useState(10);

  const handleModelChange = (nextModel: string) => {
    setModel(nextModel);
    if (MODELS_NEEDING_MORE_TOKENS.includes(nextModel)) {
      setMaxTokens((current) => Math.max(current, MIN_TOKENS_FOR_RECENT_MODELS));
    }
  };

  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);
    getAiSettings()
      .then((data) => {
        setSettings(data);
        setModel(data.anthropicModel ?? DEFAULT_MODEL);
        setMaxTokens(data.anthropicMaxTokens || 4096);
        setMaxPages(data.anthropicMaxPagesPerDocument || 10);
      })
      .catch(() => setLoadError('Não foi possível carregar as definições de IA.'))
      .finally(() => setIsLoading(false));
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const updated = await saveAiSettings({
        anthropicApiKey: apiKeyInput.trim() || undefined,
        anthropicModel: model,
        anthropicMaxTokens: maxTokens,
        anthropicMaxPagesPerDocument: maxPages,
      });
      setSettings(updated);
      setApiKeyInput('');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      setSaveError('Não foi possível guardar as definições. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearKey = async () => {
    if (!window.confirm('Tem a certeza que pretende remover a API Key da Anthropic?')) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const updated = await saveAiSettings({ clearAnthropicApiKey: true });
      setSettings(updated);
    } catch {
      setSaveError('Não foi possível remover a API Key.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-secondary-text py-8 text-center" style={{ fontSize: 'var(--text-base)' }}>
        A carregar definições...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-destructive py-4" style={{ fontSize: 'var(--text-base)' }}>
        {loadError}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Aviso se não configurado */}
      {!settings?.hasAnthropicApiKey && (
        <div
          className="flex items-start gap-3 px-4 py-3"
          style={{ borderRadius: 'var(--radius)', border: '1px solid #fbbf24', backgroundColor: '#fffbeb' }}
        >
          <Warning20Regular className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#d97706' }} />
          <div>
            <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>
              API Key não configurada
            </p>
            <p className="text-secondary-text mt-1" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
              Sem API Key da Anthropic, o processamento de plantas técnicas usará o pipeline Azure (se configurado).
              Configure a key abaixo para activar o Claude Vision.
            </p>
          </div>
        </div>
      )}

      {/* API Key */}
      <div>
        <label
          className="block text-foreground mb-2"
          style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
        >
          Anthropic API Key
        </label>

        {settings?.hasAnthropicApiKey && (
          <div className="flex items-center gap-3 mb-2">
            <span
              className="px-3 py-1 border border-border bg-secondary text-secondary-text font-mono"
              style={{ borderRadius: 'var(--radius)', fontSize: 'var(--text-caption)' }}
            >
              sk-ant-••••••••••••••••
            </span>
            <span className="text-emerald-600" style={{ fontSize: 'var(--text-caption)' }}>
              ✓ Configurada
            </span>
            <button
              type="button"
              onClick={handleClearKey}
              disabled={isSaving}
              className="px-2 py-1 border border-destructive/30 text-destructive hover:bg-destructive/10 rounded transition-colors text-xs disabled:opacity-50"
              style={{ borderRadius: 'var(--radius)', fontSize: 'var(--text-caption)' }}
            >
              Remover
            </button>
          </div>
        )}

        <div className="relative">
          <input
            type={showApiKey ? 'text' : 'password'}
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
            placeholder={settings?.hasAnthropicApiKey ? 'Insira nova key para substituir' : 'sk-ant-...'}
            className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary pr-10"
            style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)' }}
          />
          <button
            type="button"
            onClick={() => setShowApiKey((prev) => !prev)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-secondary-text hover:text-foreground"
            tabIndex={-1}
          >
            {showApiKey ? <EyeOff20Regular className="w-4 h-4" /> : <Eye20Regular className="w-4 h-4" />}
          </button>
        </div>
        <p className="mt-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
          Obtenha a sua key em{' '}
          <a
            href="https://console.anthropic.com/settings/keys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            console.anthropic.com
          </a>
          . Os custos de processamento são debitados directamente na sua conta Anthropic.
        </p>
      </div>

      {/* Modelo */}
      <div>
        <label
          className="block text-foreground mb-2"
          style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
        >
          Modelo Claude
        </label>
        <select
          value={model}
          onChange={(e) => handleModelChange(e.target.value)}
          className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)' }}
        >
          {ANTHROPIC_MODELS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      {/* Máx. tokens */}
      <div>
        <label
          className="block text-foreground mb-2"
          style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
        >
          Máx. tokens de output
        </label>
        <input
          type="number"
          value={maxTokens}
          onChange={(e) => setMaxTokens(parseInt(e.target.value, 10) || 4096)}
          min={1024}
          max={64000}
          step={512}
          className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)', maxWidth: 200 }}
        />
        <p className="mt-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
          Default: 4096. Máximo: 64000. Plantas técnicas densas e mapas grandes → use 64000 (custa mais).
        </p>
      </div>

      {/* Máx. páginas */}
      <div>
        <label
          className="block text-foreground mb-2"
          style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
        >
          Máx. páginas por documento
        </label>
        <input
          type="number"
          value={maxPages}
          onChange={(e) => setMaxPages(parseInt(e.target.value, 10) || 10)}
          min={1}
          max={50}
          className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)', maxWidth: 200 }}
        />
        <p className="mt-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
          Default: 10. PDFs com mais páginas são truncados.
        </p>
      </div>

      {/* Erros e feedback */}
      {saveError && (
        <div className="text-destructive text-sm">{saveError}</div>
      )}
      {saveSuccess && (
        <div className="text-emerald-600 text-sm">✓ Definições guardadas com sucesso.</div>
      )}

      {/* Botão guardar */}
      <div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors disabled:opacity-60"
          style={{ borderRadius: 'var(--radius-button)' }}
        >
          {isSaving ? 'A guardar...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}
